import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Loader2,
  Printer,
  Calendar,
  ChevronRight,
  Building2,
  Cog,
  Brain,
  AlertTriangle,
  Lightbulb,
  Shield,
  ArrowRight,
  BarChart3,
  TrendingUp,
  Lock,
  CheckCircle2
} from "lucide-react";
import axios from "axios";
import {
  ComplexityChart,
  PressureReadinessChart,
  RiskIndicatorsChart,
  OpportunityChart,
  BusinessImpactNarratives
} from "@/components/report";
import { useAuth } from "@/context/AuthContext";
import { checkPricingEligibility } from "@/api/aimeansbusiness";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_aianalytics-4/artifacts/d89kgck4_A_I_Means_Business.jpg";

/**
 * AI Business Evaluation Report v1.1
 * 
 * An 8-section report with executive-grade visualizations:
 * - 4 Charts: Complexity, Pressure/Readiness, Risk, Opportunity
 * - 3 Business Impact Narratives
 * - Professional print-ready format
 * 
 * Tone: Calm, grounded, plainspoken, non-judgmental, non-alarmist
 */

// Report Header Component (appears on every section for print)
const ReportHeader = ({ businessName, contactName, printMetadata }) => (
  <div className="report-header flex items-center justify-between border-b border-slate-700 pb-4 mb-6 print:border-slate-300">
    <div className="flex items-center gap-3">
      {/* Logo for screen */}
      <div className="border-2 border-primary px-3 py-1 print:hidden">
        <span className="font-heading text-lg font-bold text-white tracking-wide">A.I. MEANS</span>
        <span className="font-heading text-lg font-bold text-white tracking-wide block -mt-1">BUSINESS</span>
      </div>
      {/* Logo image for print */}
      <img 
        src="https://customer-assets.emergentagent.com/job_aianalytics-4/artifacts/83l8en3q_A_I_Means_Business.png" 
        alt="A.I. Means Business"
        className="hidden print:block h-12 w-auto"
      />
    </div>
    <div className="text-right text-sm">
      <p className="text-slate-400 print:text-slate-600">Prepared for</p>
      <p className="text-white print:text-slate-900 font-medium">{businessName}</p>
      <p className="text-slate-500 print:text-slate-600 text-xs">{contactName}</p>
      {printMetadata?.date && (
        <p className="text-slate-600 text-xs mt-1 hidden print:block">{printMetadata.date}</p>
      )}
    </div>
  </div>
);

// Report Footer Component (appears on every page for print)
const ReportFooter = ({ printMetadata, businessName }) => {
  const currentYear = new Date().getFullYear();
  return (
    <div className="report-footer text-center text-xs text-slate-500 print:text-slate-600 pt-6 mt-8 border-t border-slate-800 print:border-slate-300">
      {/* Screen version */}
      <div className="print:hidden">
        <p>{printMetadata?.copyright || `Copyright © ${currentYear}, Use What You Got Publishing, LLC`}</p>
        <p>{printMetadata?.website || "www.AIMeansBusiness.net"}</p>
      </div>
      {/* Print version - centered bold text */}
      <div className="hidden print:block">
        <p className="font-bold text-slate-700">
          This Report Created Exclusively For {businessName || "Your Business"}. Copyright © {currentYear} Use What You Got Publishing, LLC
        </p>
        <p className="font-bold text-slate-700">
          A.I. Means Business – www.AIMeansBusiness.net – (719) 205-6381
        </p>
      </div>
    </div>
  );
};

// Section Wrapper for consistent styling
const ReportSection = ({ number, title, icon: Icon, children, className = "" }) => (
  <div className={`report-section bg-navy-900/30 print:bg-white rounded-xl p-6 md:p-8 mb-6 print:break-inside-avoid print:border print:border-slate-200 ${className}`}>
    <div className="flex items-center gap-3 mb-4">
      {Icon && (
        <div className="w-10 h-10 rounded-lg bg-primary/10 print:bg-slate-100 flex items-center justify-center">
          <Icon className="w-5 h-5 text-primary print:text-slate-700" />
        </div>
      )}
      <div>
        <p className="text-primary print:text-slate-600 text-xs font-medium uppercase tracking-wider">Section {number}</p>
        <h2 className="font-heading text-xl font-semibold text-white print:text-slate-900">{title}</h2>
      </div>
    </div>
    {children}
  </div>
);

const EvaluationPage = () => {
  const { leadId } = useParams();
  const { token, user } = useAuth();
  const [evaluation, setEvaluation] = useState(null);
  const [lead, setLead] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pricingEligible, setPricingEligible] = useState(false);

  useEffect(() => {
    const fetchEvaluation = async () => {
      try {
        // Build headers - include auth token if available for pricing eligibility check
        const headers = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        
        const [leadRes, evalRes] = await Promise.all([
          axios.get(`${API}/public/lead/${leadId}`),
          axios.get(`${API}/public/evaluation/${leadId}`, { headers })
        ]);
        setLead(leadRes.data);
        setEvaluation(evalRes.data);
        
        // Check pricing eligibility (requires evaluation + SER)
        if (token) {
          try {
            const eligibilityRes = await checkPricingEligibility();
            setPricingEligible(eligibilityRes.pricing_eligible);
          } catch (eligErr) {
            console.error("Failed to check pricing eligibility:", eligErr);
            setPricingEligible(false);
          }
        }
        
      } catch (err) {
        setError("Unable to load your evaluation. Please ensure you have completed the questionnaire.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchEvaluation();
  }, [leadId, token]);

  const handlePrint = () => {
    toast.info(
      "For best results: In print settings, disable 'Headers and footers' to remove the URL from the page.",
      { duration: 6000 }
    );
    setTimeout(() => window.print(), 500);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-white font-heading text-xl">Loading Your Evaluation Report...</p>
          <p className="text-slate-400 text-sm mt-2">This may take a moment</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <h1 className="font-heading text-2xl font-bold text-white mb-2">Unable to Load Report</h1>
          <p className="text-slate-400 mb-6">{error}</p>
          <Link to="/get-started">
            <Button className="bg-primary hover:bg-primary/90 text-white">
              Start Your Evaluation
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const {
    report_meta,
    section_1_executive_overview,
    section_2_business_profile,
    section_3_operational_complexity,
    section_4_ai_posture,
    section_5_risk_assessment,
    section_6_strategic_considerations,
    section_7_limitations,
    section_8_next_step,
    visuals,
    business_impact_narratives,
    print_metadata
  } = evaluation || {};

  return (
    <>
      {/* Print Styles - Executive-grade, white background, Times New Roman 14pt */}
      <style>{`
        @media print {
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          /* Hide browser-generated header/footer (URL, title, date) */
          @page {
            size: letter;
            margin: 0.75in 0.75in 0.75in 0.75in;
          }
          
          html, body {
            background: white !important;
            color: #1e293b !important;
            font-family: 'Times New Roman', Times, serif !important;
            font-size: 14pt !important;
            line-height: 1.5 !important;
          }
          
          /* Hide any browser chrome */
          html {
            margin: 0 !important;
            padding: 0 !important;
          }
          
          .no-print {
            display: none !important;
          }
          
          .print-only {
            display: block !important;
          }
          
          .report-container {
            max-width: 100% !important;
            padding: 0 !important;
          }
          
          .report-section {
            break-inside: avoid;
            page-break-inside: avoid;
            margin-bottom: 1rem;
            background: white !important;
            border: 1px solid #e2e8f0 !important;
            border-radius: 0 !important;
            padding: 1.5rem !important;
          }
          
          /* Chart containers - force visibility and sizing for print */
          .chart-container {
            break-inside: avoid;
            page-break-inside: avoid;
            min-height: 300px !important;
          }
          
          /* Force Recharts SVGs to render in print */
          .recharts-wrapper {
            display: block !important;
            visibility: visible !important;
          }
          
          .recharts-surface {
            display: block !important;
            visibility: visible !important;
          }
          
          /* Make SVG charts visible with proper colors for print */
          svg {
            display: block !important;
            visibility: visible !important;
            max-width: 100% !important;
          }
          
          .recharts-radar-polygon {
            fill: #d97706 !important;
            fill-opacity: 0.3 !important;
            stroke: #d97706 !important;
          }
          
          .recharts-polar-grid-angle line,
          .recharts-polar-grid-concentric circle {
            stroke: #94a3b8 !important;
          }
          
          .recharts-bar-rectangle {
            fill: currentColor !important;
          }
          
          .recharts-cartesian-grid line {
            stroke: #e2e8f0 !important;
          }
          
          .recharts-reference-area rect {
            fill-opacity: 0.2 !important;
          }
          
          .recharts-scatter-symbol circle {
            fill: #d97706 !important;
            stroke: #1e293b !important;
          }
          
          /* Text colors for print */
          .text-white, h1, h2, h3, h4 {
            color: #1e293b !important;
          }
          
          .text-slate-300, .text-slate-400 {
            color: #475569 !important;
          }
          
          .text-slate-500 {
            color: #64748b !important;
          }
          
          .text-primary {
            color: #b45309 !important;
          }
          
          /* Background adjustments */
          .bg-navy-900\\/30, .bg-navy-950\\/50, .bg-navy-950\\/80 {
            background: #f8fafc !important;
          }
          
          .bg-primary\\/10 {
            background: #fef3c7 !important;
          }
          
          /* Borders */
          .border-slate-700, .border-slate-800 {
            border-color: #e2e8f0 !important;
          }
          
          .border-primary\\/30, .border-primary\\/20 {
            border-color: #d97706 !important;
          }
          
          @page :first {
            margin-top: 0.5in;
          }
          
          h1, h2, h3 {
            break-after: avoid;
            page-break-after: avoid;
          }
          
          .page-break {
            break-before: page;
            page-break-before: always;
          }
          
          /* Charts need special handling */
          .recharts-wrapper {
            max-width: 100% !important;
          }
          
          /* Title page styles */
          .print-title-page {
            display: flex !important;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            min-height: 85vh;
            text-align: center;
            page-break-after: always;
            padding-top: 2in;
          }
          
          .print-title-page h1 {
            font-size: 28pt !important;
            font-family: 'Times New Roman', Times, serif !important;
            margin-bottom: 0.25in;
            margin-top: 0.5in;
          }
          
          .print-title-page .subtitle {
            font-size: 18pt !important;
            color: #64748b !important;
          }
          
          .print-title-page img {
            max-width: 350px !important;
            height: auto !important;
          }
          
          /* Report footer for print - bold centered text */
          .report-footer {
            margin-top: 1.5rem !important;
            padding-top: 1rem !important;
            border-top: 1px solid #e2e8f0 !important;
          }
          
          .report-footer p {
            font-weight: bold !important;
            color: #374151 !important;
            font-size: 10pt !important;
            line-height: 1.4 !important;
          }
          
          /* Footer on each page */
          .print-page-footer {
            position: fixed;
            bottom: 0.5in;
            left: 0;
            right: 0;
            text-align: center;
            font-size: 10pt !important;
            color: #94a3b8 !important;
          }
        }
        
        @media screen {
          .print-only, .print-title-page {
            display: none;
          }
        }
      `}</style>

      <div className="min-h-screen bg-background print:bg-white overflow-x-hidden">
        {/* Print-only Title Page */}
        <div className="print-title-page print-only">
          {/* Main Logo */}
          <img 
            src="https://customer-assets.emergentagent.com/job_aianalytics-4/artifacts/jidsz3g4_A_I_Means_Business_R_Dale_Brunet.jpg" 
            alt="A.I. Means Business with R. Dale Brunet"
            className="max-w-md w-full h-auto mb-8 rounded-lg shadow-lg"
          />
          
          <h1 className="font-heading text-slate-900">{print_metadata?.title || "A.I. Business Evaluation Report"}</h1>
          <p className="subtitle text-slate-600">{print_metadata?.subtitle}</p>
          <p className="text-slate-600 mt-8">{print_metadata?.date}</p>
          <p className="text-slate-500 text-sm mt-4 max-w-md italic">{print_metadata?.confidentiality_notice}</p>
          
          {/* Footer on title page */}
          <div className="mt-auto pt-12 text-center">
            <p className="font-bold text-slate-700 text-sm">
              This Report Created Exclusively For {report_meta?.business_name || "Your Business"}. Copyright © {new Date().getFullYear()} Use What You Got Publishing, LLC
            </p>
            <p className="font-bold text-slate-700 text-sm">
              A.I. Means Business – www.AIMeansBusiness.net – (719) 205-6381
            </p>
          </div>
        </div>

        {/* Screen Header (hidden in print) */}
        <header className="no-print fixed top-0 left-0 right-0 z-50 bg-navy-950/95 backdrop-blur border-b border-slate-800">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
            <Link to="/portal" className="flex items-center gap-2 flex-shrink-0">
              <img 
                src={LOGO_URL}
                alt="A.I. Means Business"
                className="h-12 sm:h-16 md:h-20 w-auto"
              />
            </Link>
            <div className="flex items-center gap-2 sm:gap-3">
              <Link to="/portal">
                <Button
                  variant="outline"
                  size="sm"
                  className="border-slate-700 text-slate-300 hover:text-white text-xs sm:text-sm px-2 sm:px-3"
                  data-testid="return-home-btn"
                >
                  <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 rotate-180" />
                  <span className="hidden sm:inline">Return to Homepage</span>
                  <span className="sm:hidden">Back</span>
                </Button>
              </Link>
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrint}
                className="border-slate-700 text-slate-300 hover:text-white text-xs sm:text-sm px-2 sm:px-3"
                data-testid="print-report-btn"
              >
                <Printer className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Print / Save PDF</span>
                <span className="sm:hidden">PDF</span>
              </Button>
            </div>
          </div>
        </header>

        {/* Report Content - pt accounts for fixed header */}
        <main className="report-container max-w-5xl mx-auto px-4 sm:px-6 pt-20 sm:pt-24 md:pt-28 py-6 sm:py-8 print:px-0 print:py-0 print:pt-0">
          {/* Page Title (Screen Only) */}
          <div className="mb-4 sm:mb-6 no-print">
            <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-white tracking-tight break-words">
              A.I. Means Business
            </h1>
            <h2 className="font-heading text-xl sm:text-2xl md:text-3xl font-bold text-primary mt-1">
              Your Evaluation Report
            </h2>
          </div>
          
          {/* Report Header */}
          <ReportHeader 
            businessName={report_meta?.business_name || lead?.business_name} 
            contactName={report_meta?.contact_name || lead?.contact_name}
            printMetadata={print_metadata}
          />

          {/* Version Badge */}
          <div className="flex items-center gap-2 mb-6 no-print">
            <span className="px-2 py-1 bg-primary/20 text-primary text-xs font-medium rounded">
              Report {report_meta?.version || 'v1.1'}
            </span>
            {report_meta?.ai_generated && (
              <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs font-medium rounded">
                AI-Enhanced
              </span>
            )}
          </div>

          {/* SECTION 1: Executive Overview */}
          <ReportSection number="1" title="Executive Overview" icon={Building2}>
            <p className="text-slate-300 print:text-slate-700 leading-relaxed mb-4">
              {section_1_executive_overview?.introduction}
            </p>
            <p className="text-slate-400 print:text-slate-600 text-sm italic mb-4">
              {section_1_executive_overview?.purpose}
            </p>
            {section_1_executive_overview?.themes?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-slate-800 print:border-slate-200">
                <p className="text-slate-500 print:text-slate-600 text-xs uppercase tracking-wider mb-2">Key Themes Identified</p>
                <ul className="space-y-2">
                  {section_1_executive_overview.themes.map((theme, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-slate-300 print:text-slate-700 text-sm">
                      <ChevronRight className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      {theme}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </ReportSection>

          {/* SECTION 2: Business Profile Snapshot */}
          <ReportSection number="2" title="Business Profile Snapshot" icon={Building2}>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
              <div className="p-4 bg-navy-950/50 print:bg-slate-50 rounded-lg">
                <p className="text-slate-400 print:text-slate-600 text-xs uppercase tracking-wider mb-1">Industry</p>
                <p className="text-white print:text-slate-900 font-medium">{section_2_business_profile?.industry}</p>
              </div>
              <div className="p-4 bg-navy-950/50 print:bg-slate-50 rounded-lg">
                <p className="text-slate-400 print:text-slate-600 text-xs uppercase tracking-wider mb-1">Years in Operation</p>
                <p className="text-white print:text-slate-900 font-medium">{section_2_business_profile?.years_in_operation}</p>
              </div>
              <div className="p-4 bg-navy-950/50 print:bg-slate-50 rounded-lg">
                <p className="text-slate-400 print:text-slate-600 text-xs uppercase tracking-wider mb-1">Employees</p>
                <p className="text-white print:text-slate-900 font-medium">{section_2_business_profile?.employee_count}</p>
              </div>
              <div className="p-4 bg-navy-950/50 print:bg-slate-50 rounded-lg">
                <p className="text-slate-400 print:text-slate-600 text-xs uppercase tracking-wider mb-1">Revenue Range</p>
                <p className="text-white print:text-slate-900 font-medium">{section_2_business_profile?.revenue_range}</p>
              </div>
              <div className="p-4 bg-navy-950/50 print:bg-slate-50 rounded-lg col-span-2">
                <p className="text-slate-400 print:text-slate-600 text-xs uppercase tracking-wider mb-1">Operational Style</p>
                <p className="text-white print:text-slate-900 font-medium">{section_2_business_profile?.operational_style}</p>
              </div>
            </div>
            <p className="text-slate-300 print:text-slate-700 leading-relaxed">
              {section_2_business_profile?.summary}
            </p>
          </ReportSection>

          {/* NEW: Visual Analytics Section */}
          {visuals && (
            <ReportSection number="2.5" title="Visual Analytics" icon={BarChart3} className="page-break">
              <p className="text-slate-400 print:text-slate-600 text-sm mb-6">
                The following visualizations translate your questionnaire responses into actionable context.
                These are advisory signals, not judgments.
              </p>
              
              <div className="grid gap-8 lg:grid-cols-2">
                {/* Complexity Chart */}
                <div className="chart-container bg-navy-950/30 print:bg-slate-50 p-4 rounded-lg border border-slate-700/50 print:border-slate-200">
                  <ComplexityChart data={visuals.complexity} />
                </div>
                
                {/* Pressure/Readiness Chart */}
                <div className="chart-container bg-navy-950/30 print:bg-slate-50 p-4 rounded-lg border border-slate-700/50 print:border-slate-200">
                  <PressureReadinessChart data={visuals.pressure_readiness} />
                </div>
                
                {/* Risk Indicators Chart */}
                <div className="chart-container bg-navy-950/30 print:bg-slate-50 p-4 rounded-lg border border-slate-700/50 print:border-slate-200">
                  <RiskIndicatorsChart data={visuals.risk_indicators} />
                </div>
                
                {/* Opportunity Chart */}
                <div className="chart-container bg-navy-950/30 print:bg-slate-50 p-4 rounded-lg border border-slate-700/50 print:border-slate-200">
                  <OpportunityChart data={visuals.opportunity} />
                </div>
              </div>
            </ReportSection>
          )}

          {/* NEW: Business Impact Narratives */}
          {business_impact_narratives && business_impact_narratives.length > 0 && (
            <ReportSection number="2.6" title="Business Impact Analysis" icon={TrendingUp}>
              <BusinessImpactNarratives narratives={business_impact_narratives} />
            </ReportSection>
          )}

          {/* SECTION 3: Operational Complexity & Friction */}
          <ReportSection number="3" title="Operational Complexity & Friction Indicators" icon={Cog} className="page-break">
            <div className="space-y-4">
              <div className="p-4 bg-navy-950/50 print:bg-slate-50 rounded-lg">
                <p className="text-slate-400 print:text-slate-600 text-sm mb-2">Daily Software Tools</p>
                <p className="text-white print:text-slate-900">{section_3_operational_complexity?.tool_count} tools in active use</p>
              </div>
              
              <p className="text-slate-300 print:text-slate-700 leading-relaxed">
                {section_3_operational_complexity?.tool_observation}
              </p>
              
              <p className="text-slate-300 print:text-slate-700 leading-relaxed">
                {section_3_operational_complexity?.system_reliability}
              </p>
              
              <p className="text-slate-300 print:text-slate-700 leading-relaxed">
                {section_3_operational_complexity?.data_confidence}
              </p>
              
              <div className="p-4 bg-navy-950/50 print:bg-slate-50 rounded-lg">
                <p className="text-slate-400 print:text-slate-600 text-sm mb-2">High-Effort Areas Identified</p>
                <div className="flex flex-wrap gap-2">
                  {section_3_operational_complexity?.friction_areas?.map((area, idx) => (
                    <span key={idx} className="px-3 py-1 bg-primary/20 print:bg-amber-100 text-primary print:text-amber-800 text-sm rounded-full">
                      {area}
                    </span>
                  ))}
                </div>
              </div>
              
              <p className="text-slate-300 print:text-slate-700 leading-relaxed">
                {section_3_operational_complexity?.friction_observation}
              </p>
              
              <p className="text-slate-500 print:text-slate-600 text-sm italic border-l-2 border-slate-700 print:border-slate-300 pl-4">
                {section_3_operational_complexity?.complexity_note}
              </p>
            </div>
          </ReportSection>

          {/* SECTION 4: Current A.I. Posture */}
          <ReportSection number="4" title="Current A.I. Posture" icon={Brain}>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="p-4 bg-navy-950/50 print:bg-slate-50 rounded-lg">
                <p className="text-slate-400 print:text-slate-600 text-sm mb-2">A.I. Familiarity</p>
                <p className="text-white print:text-slate-900 capitalize">{section_4_ai_posture?.familiarity_level}</p>
              </div>
              <div className="p-4 bg-navy-950/50 print:bg-slate-50 rounded-lg">
                <p className="text-slate-400 print:text-slate-600 text-sm mb-2">Current A.I. Usage</p>
                <p className="text-white print:text-slate-900 capitalize">{section_4_ai_posture?.current_usage}</p>
              </div>
            </div>
            
            <p className="text-slate-300 print:text-slate-700 leading-relaxed mb-4">
              {section_4_ai_posture?.summary}
            </p>
            
            <p className="text-slate-500 print:text-slate-600 text-sm italic border-l-2 border-slate-700 print:border-slate-300 pl-4">
              {section_4_ai_posture?.observation}
            </p>
          </ReportSection>

          {/* SECTION 5: Risk & Decision Pressure Assessment */}
          <ReportSection number="5" title="Risk & Decision Pressure Assessment" icon={AlertTriangle} className="page-break">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-navy-950/50 print:bg-slate-50 rounded-lg">
                  <p className="text-slate-400 print:text-slate-600 text-sm mb-2">Pressure Level</p>
                  <p className="text-white print:text-slate-900 capitalize">{section_5_risk_assessment?.pressure_level}</p>
                </div>
                <div className="p-4 bg-navy-950/50 print:bg-slate-50 rounded-lg">
                  <p className="text-slate-400 print:text-slate-600 text-sm mb-2">Decision Cost Perception</p>
                  <p className="text-white print:text-slate-900 capitalize">{section_5_risk_assessment?.decision_cost}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-navy-950/50 print:bg-slate-50 rounded-lg">
                  <p className="text-slate-400 print:text-slate-600 text-sm mb-2">Inaction Concern</p>
                  <p className="text-white print:text-slate-900 capitalize">{section_5_risk_assessment?.inaction_concern}</p>
                </div>
                <div className="p-4 bg-navy-950/50 print:bg-slate-50 rounded-lg">
                  <p className="text-slate-400 print:text-slate-600 text-sm mb-2">Current Priority</p>
                  <p className="text-white print:text-slate-900 capitalize">{section_5_risk_assessment?.current_priority}</p>
                </div>
              </div>
              
              <div className="p-4 bg-navy-950/50 print:bg-slate-50 rounded-lg">
                <p className="text-slate-400 print:text-slate-600 text-sm mb-2">Pressure Sources</p>
                <div className="flex flex-wrap gap-2">
                  {section_5_risk_assessment?.pressure_sources?.map((source, idx) => (
                    <span key={idx} className="px-3 py-1 bg-slate-800 print:bg-slate-200 text-slate-300 print:text-slate-700 text-sm rounded-full">
                      {source}
                    </span>
                  ))}
                </div>
              </div>
              
              <p className="text-slate-300 print:text-slate-700 leading-relaxed">
                {section_5_risk_assessment?.summary}
              </p>
              
              <p className="text-slate-300 print:text-slate-700 leading-relaxed">
                {section_5_risk_assessment?.decision_context}
              </p>
              
              <p className="text-slate-500 print:text-slate-600 text-sm italic border-l-2 border-slate-700 print:border-slate-300 pl-4">
                {section_5_risk_assessment?.observation}
              </p>
            </div>
          </ReportSection>

          {/* SECTION 6: Strategic Considerations */}
          <ReportSection number="6" title="Strategic Considerations" icon={Lightbulb} className="page-break">
            <p className="text-slate-400 print:text-slate-600 text-sm mb-4">
              The following are areas for thoughtful reflection—not prescriptive recommendations.
            </p>
            <div className="space-y-4">
              {section_6_strategic_considerations?.map((item, idx) => (
                <div key={idx} className="p-4 bg-navy-950/50 print:bg-slate-50 rounded-lg border-l-2 border-primary/50">
                  <p className="text-primary print:text-amber-700 text-sm font-medium mb-2">{item.area}</p>
                  <p className="text-slate-300 print:text-slate-700 text-sm leading-relaxed">{item.consideration}</p>
                </div>
              ))}
            </div>
          </ReportSection>

          {/* SECTION 7: What This Evaluation Does Not Do */}
          <ReportSection number="7" title="What This Evaluation Does Not Do" icon={Shield}>
            <div className="space-y-3 mb-4">
              {section_7_limitations?.statements?.map((statement, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-slate-500 mt-2 flex-shrink-0" />
                  <p className="text-slate-300 print:text-slate-700 text-sm">{statement}</p>
                </div>
              ))}
            </div>
            <p className="text-slate-500 print:text-slate-600 text-sm italic">
              {section_7_limitations?.context}
            </p>
          </ReportSection>

          {/* SECTION 8: Next Logical Step */}
          <ReportSection number="8" title="Next Logical Step" icon={ArrowRight} className="border border-primary/20">
            <p className="text-slate-300 print:text-slate-700 mb-4 leading-relaxed">
              {section_8_next_step?.introduction}
            </p>
            
            <p className="text-slate-400 print:text-slate-600 text-sm mb-6 leading-relaxed">
              {section_8_next_step?.transition}
            </p>

            {/* Recommended Service Card - PHASE 5: Full Value Framing */}
            <div className="bg-navy-950/80 print:bg-slate-100 rounded-xl overflow-hidden border border-primary/30 mb-6">
              {/* Header with Service Name and Price */}
              <div className="p-6 border-b border-slate-800 print:border-slate-300">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-primary print:text-amber-700 text-xs font-medium uppercase tracking-wider mb-1">
                      Based on Your Evaluation
                    </p>
                    <h3 className="font-heading text-xl font-semibold text-white print:text-slate-900">
                      {section_8_next_step?.recommended_service?.name}
                    </h3>
                  </div>
                  <div className="text-right">
                    {/* Price GATED - only show when eligible (evaluation + SER) */}
                    {pricingEligible ? (
                      <>
                        <p className="text-2xl font-heading font-bold text-primary print:text-amber-700" data-testid="eval-price-visible">
                          {section_8_next_step?.recommended_service?.price}
                        </p>
                        <p className="text-xs text-slate-400 print:text-slate-500 uppercase tracking-wider">Fixed, Prepaid</p>
                      </>
                    ) : (
                      <div className="flex items-center gap-2 text-slate-400 bg-slate-800/50 px-3 py-2 rounded-lg print:bg-slate-200" data-testid="eval-price-hidden">
                        <Lock className="w-4 h-4" />
                        <span className="text-sm">Revealed after your Evaluation Report</span>
                      </div>
                    )}
                  </div>
                </div>
                
                <p className="text-slate-300 print:text-slate-700 text-sm mt-4 leading-relaxed">
                  {section_8_next_step?.recommended_service?.description}
                </p>
              </div>

              {/* What to Expect (Scope Includes) */}
              <div className="p-6 border-b border-slate-800 print:border-slate-300">
                <p className="text-slate-400 print:text-slate-600 text-xs uppercase tracking-wider mb-3">What to Expect</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {section_8_next_step?.what_to_expect?.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-slate-300 print:text-slate-700 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-primary print:text-amber-700 flex-shrink-0" />
                      {item}
                    </div>
                  ))}
                </div>
              </div>

              {/* Value Anchor Callout - Only show when price is visible */}
              {pricingEligible && (
                <div className="p-6 bg-green-950/30 print:bg-green-50">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-green-500/20 print:bg-green-100 flex items-center justify-center flex-shrink-0">
                      <Shield className="w-5 h-5 text-green-400 print:text-green-700" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-green-300 print:text-green-800 text-sm mb-1">Value Investment</h4>
                      <p className="text-green-300/80 print:text-green-700 text-sm leading-relaxed">
                        This engagement is designed to prevent far greater costs associated with acting without clarity.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <p className="text-slate-400 print:text-slate-600 text-sm mb-6 text-center">
              {section_8_next_step?.closing}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center no-print">
              <Link to={`/schedule/${leadId}`} data-testid="schedule-consultation-btn">
                <Button className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-white font-semibold rounded-full px-8 py-4 text-lg shadow-[0_0_20px_rgba(217,119,6,0.3)]">
                  <Calendar className="w-5 h-5 mr-2" />
                  {section_8_next_step?.cta_text || "Schedule a Conversation"}
                </Button>
              </Link>
              <Button
                variant="outline"
                onClick={handlePrint}
                className="w-full sm:w-auto border-slate-700 text-slate-300 hover:text-white rounded-full px-8 py-4"
              >
                <Printer className="w-5 h-5 mr-2" />
                Save as PDF
              </Button>
            </div>

            {/* Return to Homepage */}
            <div className="mt-6 text-center no-print">
              <Link to="/portal" className="text-xs sm:text-sm text-slate-500 hover:text-slate-300 inline-flex items-center">
                <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 mr-1 rotate-180" />
                Return to Homepage
              </Link>
            </div>
          </ReportSection>

          {/* Report Footer */}
          <ReportFooter printMetadata={print_metadata} businessName={report_meta?.business_name || lead?.business_name} />
        </main>

        {/* Screen Footer (hidden in print) */}
        <footer className="no-print bg-navy-950 border-t border-slate-800 py-6 sm:py-8">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
            <Link to="/portal" className="inline-block mb-4">
              <img 
                src={LOGO_URL}
                alt="A.I. Means Business"
                className="h-12 sm:h-16 w-auto mx-auto"
              />
            </Link>
            <p className="text-slate-400 text-xs sm:text-sm mb-2">
              Trusted advisory for businesses navigating A.I. transformation
            </p>
            <p className="text-slate-600 text-xs">
              {print_metadata?.copyright || "Copyright © 2026, Use What You Got Publishing, LLC"} • {print_metadata?.website || "www.AIMeansBusiness.net"}
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default EvaluationPage;
